<template>
  <div
    class="star-rating-display d-inline-flex align-items-center flex-wrap"
    :class="{ 'star-rating-display--sm': small }"
    aria-hidden="true"
  >
    <span v-for="n in 5" :key="n" class="star-cell">
      <i class="ti ti-star star-bg" />
      <span class="star-fill" :style="{ width: starFillPercent(n, starsOutOfFive) + '%' }">
        <i class="ti ti-star star-fg" />
      </span>
    </span>
    <span v-if="showScore && scoreLabel" class="ms-2 rating-score text-muted">{{ scoreLabel }}</span>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useNumberFormatter } from '@/composables/useNumberFormatter';

const props = defineProps({
  /** 1–5 scale displayed directly on 5 stars. */
  scoreOneToTen: {
    type: Number,
    default: undefined
  },
  showScore: {
    type: Boolean,
    default: true
  },
  small: {
    type: Boolean,
    default: false
  },
  decimalPlaces: {
    type: Number,
    default: 2
  }
});

const { formatNumber } = useNumberFormatter();

function starFillPercent(starIndex, starsOutOfFiveValue) {
  const rem = starsOutOfFiveValue - (starIndex - 1);
  if (rem >= 1) return 100;
  if (rem >= 0.5) return 50;
  return 0;
}

const starsOutOfFive = computed(() => {
  const s = props.scoreOneToTen;
  if (s === undefined || s === null || !Number.isFinite(Number(s))) return 0;
  return Math.min(5, Math.max(0, Number(s)));
});

const scoreLabel = computed(() => {
  if (props.scoreOneToTen === undefined || props.scoreOneToTen === null || !Number.isFinite(Number(props.scoreOneToTen))) {
    return '';
  }
  const n = formatNumber(Number(props.scoreOneToTen), { decimalPlaces: props.decimalPlaces });
  return `${n}/5`;
});
</script>

<style scoped>
.star-rating-display {
  gap: 0;
  line-height: 1;
}

.star-cell {
  position: relative;
  display: inline-block;
  width: 1.4rem;
  height: 1.4rem;
  margin-right: 3px;
  vertical-align: middle;
}

.star-rating-display--sm .star-cell {
  width: 1.12rem;
  height: 1.12rem;
  margin-right: 2px;
}

.star-bg,
.star-fg {
  font-size: 1.3rem;
  line-height: 1.4rem;
}

.star-rating-display--sm .star-bg,
.star-rating-display--sm .star-fg {
  font-size: 1.05rem;
  line-height: 1.12rem;
}

.star-bg {
  position: absolute;
  left: 0;
  top: 0;
  color: var(--bs-secondary-bg, #e9ecef);
}

.star-fill {
  position: absolute;
  left: 0;
  top: 0;
  overflow: hidden;
  height: 100%;
  pointer-events: none;
}

.star-fg {
  color: #f59f00;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.12));
}

.rating-score {
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
}
</style>
